/**
 * sanitize.js
 * -----------
 * Central DOMPurify helper used everywhere HTML from the API
 * needs to be rendered via html-react-parser.
 *
 * Usage:
 *   import { sanitizeHtml } from "utils/sanitize";
 *   parse(sanitizeHtml(someHtmlString))
 *
 * XSS detection:
 *   import { containsXSS } from "utils/sanitize";
 *   if (containsXSS(formValues)) { show warning toast }
 */

import DOMPurify from "dompurify";

/**
 * Sanitize an HTML string, returning a clean string safe for
 * html-react-parser.  Only a minimal allow-list of tags/attrs
 * that are actually used in this project is permitted.
 *
 * @param {string} dirty  - Raw HTML that may come from an API response.
 * @returns {string}       - Sanitized HTML string.
 */
export const sanitizeHtml = (dirty) => {
  if (!dirty || typeof dirty !== "string") return "";

  return DOMPurify.sanitize(dirty, {
    // Permit only the structural/inline tags used across the project
    ALLOWED_TAGS: [
      "b", "strong", "i", "em", "u", "s", "br", "span",
      "p", "ul", "ol", "li", "a", "small", "sup", "sub",
    ],
    // Strip every attribute except href on <a> (for activity-log links)
    ALLOWED_ATTR: ["href", "target", "rel"],
    // Force external links to be safe
    ADD_ATTR: ["target"],
    // Prevent data: and javascript: URIs in href
    ALLOW_DATA_ATTR: false,
    FORCE_BODY: false,
  });
};

/**
 * XSS_PATTERNS
 * A list of common XSS attack signatures to check against.
 * We deliberately use both DOMPurify and a pattern list so that
 * non-HTML strings (e.g. javascript:alert(1)) are also caught.
 */
const XSS_PATTERNS = [
  /<script[\s\S]*?>/i,              // <script …>
  /<\/script>/i,                    // </script>
  /javascript\s*:/i,                // javascript:
  /vbscript\s*:/i,                  // vbscript:
  /on\w+\s*=\s*["']?[^"'>]*/i,    // onerror=, onclick=, …
  /<iframe[\s\S]*?>/i,              // <iframe …>
  /<object[\s\S]*?>/i,              // <object …>
  /<embed[\s\S]*?>/i,               // <embed …>
  /<img[^>]+src\s*=\s*["']?\s*javascript/i, // <img src="javascript:…">
  /data:\s*text\/html/i,            // data:text/html (data-URI XSS)
  /expression\s*\(/i,               // CSS expression()
  /document\s*\.\s*(cookie|write|location)/i, // DOM manipulation
  /window\s*\.\s*(location|open)/i, // window.location / window.open
  /eval\s*\(/i,                     // eval(
  /setTimeout\s*\(/i,               // setTimeout(
  /setInterval\s*\(/i,              // setInterval(
  /new\s+Function\s*\(/i,           // new Function(
  /<[^>]+(\s+|%)on\w+\s*=/i,       // encoded event handlers
];

/**
 * Check a single string value for XSS payloads.
 *
 * Two complementary methods are used:
 *  1. Regex pattern list – catches non-HTML attack strings (javascript:, eval(), …)
 *  2. DOMPurify sanitize – strips tags/attrs and compares with the original.
 *     If DOMPurify removes anything the string contained unsafe HTML.
 *
 * @param {string} value
 * @returns {boolean} true if XSS is detected
 */
const checkStringForXSS = (value) => {
  if (typeof value !== "string" || !value.trim()) return false;

  // 1. Regex check
  if (XSS_PATTERNS.some((pattern) => pattern.test(value))) return true;

  // 2. DOMPurify check – allow ALL tags so that every tag that DOMPurify
  //    removes counts as suspicious (we don't want to block normal text).
  const cleaned = DOMPurify.sanitize(value, {
    ALLOWED_TAGS: [],   // strip every tag
    ALLOWED_ATTR: [],   // strip every attribute
  });

  // If the cleaned version differs AND the original contained "<" it likely
  // had HTML/script tags that were stripped.
  if (cleaned !== value && value.includes("<")) return true;

  return false;
};

/**
 * Recursively walk any form-value structure (string | number | object | array)
 * and return true as soon as one XSS payload is detected.
 *
 * @param {*} data  – A Formik values object, array, string, or number.
 * @returns {boolean}
 */
export const containsXSS = (data) => {
  if (data === null || data === undefined) return false;

  if (typeof data === "string") {
    return checkStringForXSS(data);
  }

  if (Array.isArray(data)) {
    return data.some((item) => containsXSS(item));
  }

  if (typeof data === "object" && !(data instanceof Date)) {
    return Object.values(data).some((val) => containsXSS(val));
  }

  // numbers, booleans, Date – safe
  return false;
};
